/**
 * Hotspot Escalation Detection — monitors geopolitical hotspots for
 * multi-dimensional escalation signals.
 *
 * 4-dimension scoring:
 * - News (35%): event density near hotspot
 * - CII (25%): Country Instability Index for hotspot's region
 * - Convergence (25%): multi-source convergence near hotspot
 * - Military (15%): military-tagged events near hotspot
 *
 * Features:
 * - 48-point 24h history ring buffer per hotspot
 * - Linear regression trend calculation (rising/stable/falling)
 * - 2-hour alert cooldown per hotspot to prevent spam
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { AnalysisResult, HotspotEscalation } from './types';
import { getEventTimestamp } from './types';
import { HOTSPOTS, type Hotspot } from '@/config/geo';
import { haversineDistance } from '@/utils/geo';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const HOTSPOT_RADIUS_KM = 300; // Events within 300km contribute to hotspot score
const HISTORY_SIZE = 48; // 48 points = 30min intervals over 24h
const HISTORY_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

// Dimension weights
const WEIGHT_NEWS = 0.35;
const WEIGHT_CII = 0.25;
const WEIGHT_CONVERGENCE = 0.25;
const WEIGHT_MILITARY = 0.15;

// Military event types
const MILITARY_TYPES = new Set(['military', 'flight_position', 'vessel_position']);
const MILITARY_KEYWORDS = new Set(['military', 'troops', 'missile', 'weapon', 'armed', 'defense', 'navy', 'air force']);

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

/** Ring buffer of historical scores per hotspot. */
const _history = new Map<string, { scores: number[]; timestamps: number[] }>();

/** Last alert timestamp per hotspot for cooldown. */
const _lastAlert = new Map<string, number>();

/** Get or create history for a hotspot. */
function getHistory(hotspotId: string): { scores: number[]; timestamps: number[] } {
  let h = _history.get(hotspotId);
  if (!h) {
    h = { scores: [], timestamps: [] };
    _history.set(hotspotId, h);
  }
  return h;
}

/** Record a score into the ring buffer. */
function recordScore(hotspotId: string, score: number, timestamp: number): void {
  const h = getHistory(hotspotId);

  // Only record if enough time has passed since last record
  const lastTs = h.timestamps[h.timestamps.length - 1] ?? 0;
  if (timestamp - lastTs < HISTORY_INTERVAL_MS) return;

  h.scores.push(score);
  h.timestamps.push(timestamp);

  // Trim to ring buffer size
  while (h.scores.length > HISTORY_SIZE) {
    h.scores.shift();
    h.timestamps.shift();
  }
}

/**
 * Linear regression trend over the history buffer.
 * Returns slope normalized to -1..1 range.
 */
function calculateTrend(hotspotId: string): 'rising' | 'stable' | 'falling' {
  const h = _history.get(hotspotId);
  if (!h || h.scores.length < 4) return 'stable';

  const n = h.scores.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += h.scores[i];
    sumXY += i * h.scores[i];
    sumX2 += i * i;
  }

  const denominator = n * sumX2 - sumX * sumX;
  if (denominator === 0) return 'stable';

  const slope = (n * sumXY - sumX * sumY) / denominator;

  // Normalize slope: positive slope > 0.5 = rising, < -0.5 = falling
  if (slope > 0.5) return 'rising';
  if (slope < -0.5) return 'falling';
  return 'stable';
}

/** Check if alert cooldown is active for a hotspot. */
function isOnCooldown(hotspotId: string, now: number): boolean {
  const lastTime = _lastAlert.get(hotspotId) ?? 0;
  return (now - lastTime) < TWO_HOURS_MS;
}

// ---------------------------------------------------------------------------
// Dimension scoring
// ---------------------------------------------------------------------------

/** Score News dimension: event density within radius over 24h. */
function scoreNews(hotspot: Hotspot, events: NormalizedEvent[], cutoff: number): number {
  let count = 0;
  for (const event of events) {
    if (!event.location?.lat || !event.location?.lng) continue;
    if (getEventTimestamp(event) < cutoff) continue;
    const dist = haversineDistance(hotspot.lat, hotspot.lng, event.location.lat, event.location.lng);
    if (dist <= HOTSPOT_RADIUS_KM) count++;
  }
  // Scale: 0 events = 0, 20+ events = 100
  return Math.min(100, count * 5);
}

/** Score CII dimension: use CII score from analysis result for the hotspot's region. */
function scoreCII(hotspot: Hotspot, analysisResult: AnalysisResult): number {
  // Try to find CII for relevant country codes
  // Hotspots may span multiple countries, check by proximity
  let bestCII = 0;
  for (const [, cii] of analysisResult.ciiScores) {
    if (cii.overall > bestCII) {
      // Check if this country is relevant to the hotspot
      // Simple heuristic: if the CII country appears in the hotspot description or name
      const hotspotText = `${hotspot.name} ${hotspot.description}`.toLowerCase();
      if (hotspotText.includes(cii.countryName.toLowerCase())) {
        bestCII = cii.overall;
      }
    }
  }
  return bestCII;
}

/** Score Convergence dimension: max convergence score near the hotspot. */
function scoreConvergence(hotspot: Hotspot, analysisResult: AnalysisResult): number {
  let bestScore = 0;
  for (const conv of analysisResult.convergences) {
    const dist = haversineDistance(hotspot.lat, hotspot.lng, conv.lat, conv.lng);
    if (dist <= HOTSPOT_RADIUS_KM && conv.score > bestScore) {
      bestScore = conv.score;
    }
  }
  return bestScore;
}

/** Score Military dimension: military-tagged events near hotspot. */
function scoreMilitary(hotspot: Hotspot, events: NormalizedEvent[], cutoff: number): number {
  let count = 0;
  for (const event of events) {
    if (!event.location?.lat || !event.location?.lng) continue;
    if (getEventTimestamp(event) < cutoff) continue;

    const isMilitaryType = MILITARY_TYPES.has(event.type);
    const hasMilitaryKeyword = event.tags?.some(t => MILITARY_KEYWORDS.has(t.toLowerCase())) ?? false;
    const titleWords = event.title.toLowerCase().split(/\s+/);
    const titleHasMilitary = titleWords.some(w => MILITARY_KEYWORDS.has(w));

    if (!isMilitaryType && !hasMilitaryKeyword && !titleHasMilitary) continue;

    const dist = haversineDistance(hotspot.lat, hotspot.lng, event.location.lat, event.location.lng);
    if (dist <= HOTSPOT_RADIUS_KM) count++;
  }
  // Scale: 0 = 0, 10+ military events = 100
  return Math.min(100, count * 10);
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Detect hotspot escalations across all configured geopolitical hotspots.
 *
 * @param events - All current events in the store
 * @param analysisResult - Results from other analysis modules (CII, convergence)
 * @returns Array of HotspotEscalation results, sorted by score descending
 */
export function detectHotspotEscalations(
  events: NormalizedEvent[],
  analysisResult: AnalysisResult,
): HotspotEscalation[] {
  const now = Date.now();
  const cutoff = now - TWENTY_FOUR_HOURS_MS;
  const results: HotspotEscalation[] = [];

  for (const hotspot of HOTSPOTS as readonly Hotspot[]) {
    const news = scoreNews(hotspot, events, cutoff);
    const cii = scoreCII(hotspot, analysisResult);
    const convergence = scoreConvergence(hotspot, analysisResult);
    const military = scoreMilitary(hotspot, events, cutoff);

    const score = Math.round(
      news * WEIGHT_NEWS +
      cii * WEIGHT_CII +
      convergence * WEIGHT_CONVERGENCE +
      military * WEIGHT_MILITARY
    );

    // Record into history ring buffer
    recordScore(hotspot.id, score, now);

    // Calculate trend from history
    const trend = calculateTrend(hotspot.id);

    // Check alert cooldown
    const cooldown = isOnCooldown(hotspot.id, now);

    // Update alert timestamp if score is significant and not on cooldown
    if (score >= 30 && !cooldown) {
      _lastAlert.set(hotspot.id, now);
    }

    // Only report hotspots with meaningful activity
    if (score < 5) continue;

    results.push({
      hotspotId: hotspot.id,
      hotspotName: hotspot.name,
      lat: hotspot.lat,
      lng: hotspot.lng,
      dimensions: { news, cii, convergence, military },
      score,
      trend,
      alertCooldown: cooldown,
      detectedAt: new Date().toISOString(),
    });
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return results;
}
